/**
 * Admin preview of a preset's members (gm#143).
 *
 * The surface that lets a site-admin eyeball a companion parent group's
 * DERIVED members without revealing it to the parents in it. It is backed by
 * the read-only admin endpoint added in apis#1166
 * (`GET /group/admin/presets/{id}/membership`), which ignores the member-list
 * gate and suppresses nothing.
 *
 * The whole point is that this is NOT the reveal control:
 *
 *   - Revealing (`PresetCompanionSection`'s visibility row) is a PERSISTED
 *     write. It flips `ShowMemberList` on both documents and bumps
 *     `VisibilityRevision`, and from then on every parent in the group can see
 *     the other families in it.
 *   - Previewing changes nothing at all. It is a read, visible only to the
 *     administrator who asked for it.
 *
 * Those two must never be confused, so this dialog states what it does not do
 * in its own copy, carries no visibility control of its own, and lives in a
 * separate surface from the reveal row rather than beside it. The reveal
 * control and its warning copy are deliberately untouched by this component.
 *
 * Every row here is a person, so: the query is issued only while this
 * component is mounted (the page mounts it on demand, not on page load), the
 * rows are never logged, and the API pages them at 100.
 *
 * All fixture data in the tests is synthetic.
 */
import { useEffect, useState } from "react";
import {
  IonButton,
  IonContent,
  IonHeader,
  IonIcon,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import {
  informationCircleOutline,
  lockClosedOutline,
  warningOutline,
} from "ionicons/icons";
import { usePresetMembership } from "../../hooks/usePresets";
import { parsePresetError } from "./presetErrorParser";
import {
  PRESET_MEMBERSHIP_PAGE_MAX,
  type Preset,
  type PresetMembershipMember,
} from "../../types/preset";

import "./presets.css";

interface PresetMembershipModalProps {
  preset: Preset;
  onDismiss: () => void;
}

/** What to do about a failed read, by status. The three the endpoint actually
 *  distinguishes need different actions from the operator, and "refresh to try
 *  again" is wrong for two of them - it is the retry that keeps a rate limit
 *  closed, and no amount of refreshing brings back a deleted preset. */
function membershipErrorHint(status?: number): string {
  if (status === 403) {
    return "Previewing members requires site-admin access.";
  }
  if (status === 404) {
    return "This preset no longer exists - it may have been deleted since this page was opened.";
  }
  if (status === 429) {
    return "Too many member reads in a short period. Wait, then try again - retrying now keeps the limit closed.";
  }
  return "Refresh to try again.";
}

/** Row label. A missing name means the contact document is gone or unnamed,
 *  not that the row is spurious - so name the gap and keep the id, which is
 *  the only handle an operator has for chasing it up. */
function memberLabel(member: PresetMembershipMember): string {
  const label = member.label?.trim();
  if (label) return label;
  const shortId = member.contact_id.includes("/")
    ? member.contact_id.split("/", 2)[1]
    : member.contact_id;
  return `Contact ${shortId} (no name on record)`;
}

export function PresetMembershipModal({
  preset,
  onDismiss,
}: PresetMembershipModalProps) {
  const [skip, setSkip] = useState(0);
  const membership = usePresetMembership(preset.id, {
    limit: PRESET_MEMBERSHIP_PAGE_MAX,
    skip,
  });

  const data = membership.data ?? null;
  const members = data?.members ?? [];
  const total = data?.member_count ?? 0;
  const pageSize = data?.limit ?? PRESET_MEMBERSHIP_PAGE_MAX;
  const isDerived = !!preset.derived_from;

  const parsedError = membership.isError
    ? parsePresetError(membership.error)
    : null;
  const errorTitle = membership.isError
    ? parsedError?.title ?? "The member list could not be loaded"
    : null;
  const errorStatus = (
    membership.error as { response?: { status?: number } } | null | undefined
  )?.response?.status;

  // The materialiser's own recorded figure sits next to the live one on the
  // wire precisely so a disagreement is visible. It means the membership has
  // moved since that run - normal between hourly runs, but worth saying out
  // loud on a screen whose job is "is this group right?".
  const recorded = data?.last_member_count ?? null;
  const countsDisagree = !!data && recorded !== null && recorded !== total;

  // Page off the window the SERVER reported, not the one we asked for: the two
  // differ whenever it caps or normalises the request, and every arithmetic
  // below (the range label, both buttons) would then describe a page nobody is
  // looking at.
  const pageStart = data?.skip ?? skip;
  const lastPageStart =
    total === 0 ? 0 : Math.floor((total - 1) / pageSize) * pageSize;
  const firstShown = members.length === 0 ? 0 : pageStart + 1;
  const lastShown = pageStart + members.length;
  const hasPrev = pageStart > 0;
  const hasNext = lastShown < total;

  // `member_count` is LIVE, so the roster can shrink out from under a page an
  // operator is already on - the materialiser runs hourly and a source cohort
  // edit re-derives it. Left alone that strands them on an empty window with a
  // disabled Next and a nonsense range, so walk back to the last page that
  // exists. Converges: the target is always below the current total.
  useEffect(() => {
    if (!data) return;
    if (pageStart > lastPageStart && skip !== lastPageStart) {
      setSkip(lastPageStart);
    }
  }, [data, pageStart, lastPageStart, skip]);

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Members - admin preview</IonTitle>
          <IonButton slot="end" fill="clear" onClick={onDismiss}>
            Close
          </IonButton>
        </IonToolbar>
      </IonHeader>
      <IonContent className="preset-modal-content">
        {/* Said before anything else, and said as what it does NOT do: the
            control this replaces is a durable privacy change, and an operator
            arriving here from that row needs to know immediately that this one
            is not. */}
        <div className="preset-membership-notice" role="note">
          <IonIcon icon={lockClosedOutline} aria-hidden="true" />
          <span>
            <strong>Read-only preview for administrators.</strong> Opening this
            changes nothing: members of this group cannot see each other as a
            result, and its member-list visibility is unchanged. To change what
            members can see, use the member-list control on the preset page -
            that one is a saved change.
          </span>
        </div>

        {data && !data.show_member_list && (
          <p className="preset-companion-note">
            This group's member list is hidden from its own members. You can see
            it here because you are a site administrator.
          </p>
        )}

        {errorTitle && (
          <div className="preset-modal-error" role="alert">
            <strong>{errorTitle}</strong>
            {/* The parser's bullets carry the server's own detail - notably
                retry_after_seconds on a 429, which is the one number that
                tells the operator when retrying is worth anything. */}
            {parsedError?.bullets.length ? (
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {parsedError.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            ) : null}
            <p style={{ margin: "6px 0 0" }}>
              {membershipErrorHint(errorStatus)}
            </p>
          </div>
        )}

        {membership.isLoading && <p>Loading members...</p>}

        {data && (
          <>
            <div className="preset-companion-counts">
              <span className="preset-companion-count">
                Members now: <strong>{total}</strong>
              </span>
              {recorded !== null && (
                <span className="preset-companion-count">
                  Last materialised run recorded: <strong>{recorded}</strong>
                </span>
              )}
            </div>

            {countsDisagree && (
              <div className="preset-companion-warning" role="status">
                <IonIcon icon={informationCircleOutline} aria-hidden="true" />
                <span>
                  The membership has changed since the last materialisation.
                  The list below is the current one; re-materialise the
                  {isDerived ? " source preset" : " preset"} if you expected
                  these to match.
                </span>
              </div>
            )}

            {total === 0 ? (
              <div className="preset-companion-warning" role="status">
                <IonIcon icon={warningOutline} aria-hidden="true" />
                <span>
                  This group has no members.
                  {isDerived
                    ? " Its members are derived from the carers of the source preset's STUDENT members, so a source made up of staff, or of groups with no students, derives nobody."
                    : " Nothing sent to it reaches anyone."}
                </span>
              </div>
            ) : members.length === 0 ? (
              // The group still has members, but not on this page - it shrank
              // while it was being read. The effect above is already walking
              // back to the last page that exists; showing a range and two
              // buttons for a window that no longer exists would only invite
              // a click into it.
              <p>The member list changed while you were reading it - reloading...</p>
            ) : (
              <>
                <ul className="preset-membership-list">
                  {members.map((member) => (
                    <li
                      key={member.contact_id}
                      className="preset-membership-row"
                    >
                      {memberLabel(member)}
                    </li>
                  ))}
                </ul>
                <div className="preset-membership-pager">
                  <span className="preset-membership-range">
                    Showing {firstShown}-{lastShown} of {total}
                  </span>
                  <IonButton
                    fill="outline"
                    size="small"
                    disabled={!hasPrev || membership.isFetching}
                    onClick={() => setSkip(Math.max(0, pageStart - pageSize))}
                  >
                    Previous
                  </IonButton>
                  <IonButton
                    fill="outline"
                    size="small"
                    disabled={!hasNext || membership.isFetching}
                    onClick={() => setSkip(pageStart + pageSize)}
                  >
                    Next
                  </IonButton>
                </div>
              </>
            )}
          </>
        )}

        <div className="preset-modal-actions">
          <IonButton fill="outline" onClick={onDismiss}>
            Close
          </IonButton>
        </div>
      </IonContent>
    </>
  );
}
